import { useEffect, useMemo, useRef, useState } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as launcherTables, type DbConnection as LauncherDbConnection } from '@sdk/launcher';
import {
  addBaseUrlToProtocol,
  detectPlatform,
  getLauncherDownloadUrl,
  getStatusMessage,
  httpLaunch,
  resolveHelperState,
  resolveLaunchVersion,
  resolveOrgApp,
  startHelper,
  type HelperState,
} from '../services/launcher/launchService';
import { useLauncherProbe } from '../services/launcher/launcherProbeStore';
import {
  channelLabel,
  composeLaunchArgs,
  configuredChannels,
  resolveChannelArgs,
} from '../services/launcher/launcherArgs';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { useLauncherChannel } from '../hooks/useLauncherChannel';
import { LAUNCHER_CHANNELS, type LauncherChannel } from '../hooks/useLauncherReadiness';
import { useAuthKit } from '../contexts/AuthProvider';
import {
  setStoredLaunchAppId,
  useStoredLaunchAppId,
} from '../services/launcher/launchAppChoice';
import {
  isDownloadActive,
  latestAppDownload,
  resolveInstallState,
} from '../services/launcher/launchService';
import { LAUNCHER_MODULE } from '../contexts/SpacetimeProvider';
import { WEBCLIENT_HOST } from '../services/orgSession';
import { useOrgSession } from '../contexts/OrgContext';
import { GatedButton } from './GatedButton';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from './Icon';
import { useProjects } from '../hooks/useProjects';
import { useTweaks } from '../hooks/useTweaks';

const MINEZERO_APP_ID = import.meta.env.VITE_MINEZERO_APP_ID ?? 'minezero';
const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI;
// The stamp's LAUNCHER WorkOS client (distinct from the webclient one) — the
// launcher EXE authenticates with it. Public-by-design, like the other ids.
const LAUNCHER_WORKOS_CLIENT_ID =
  import.meta.env.VITE_WORKOS_LAUNCHER_CLIENT_ID ?? 'client_01KWHRKYFJW6DGYBKJKQ8HQ7S7';

export function LauncherButton() {
  const launcherConn = useSpacetimeDB('launcher');
  const { user, organizationId } = useAuthKit();
  const { org } = useOrgSession();
  const orgSlug = org.slug;
  // Topbar's current site: sent as -TurboTwinProject= so the app skips its
  // project selector and lands straight in the site the user is looking at.
  const { sites } = useProjects();
  const { tweaks } = useTweaks();
  const projectName = (sites[tweaks.siteIndex] ?? sites[0])?.projectName ?? null;
  const [open, setOpen] = useState(false);
  // Channel selection is shared with the per-row Launch button on TraineePortal
  // (and can be pinned by a VITE_MINEZERO_CHANNEL deployment override).
  const { channel, setChannel, envForced } = useLauncherChannel();
  const [launchState, setLaunchState] = useState<
    | { kind: 'idle' }
    | { kind: 'launching' }
    | { kind: 'sent'; via: 'http' | 'stdb' }
    | { kind: 'downloading' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapperRef, open, () => setOpen(false));

  // Watched tables per spec §C — `my_org_*` views (org-scoped, public)
  // rather than the raw `app` / `app_version` / `channel_version`
  // tables, which are private on the deployed turbotwin-launcher and
  // were failing the subscription batch (red rocket). Row shapes are
  // identical; only the access scope differs.
  const [apps] = useTable('launcher', launcherTables.my_org_apps);
  const [appVersions] = useTable('launcher', launcherTables.my_org_app_versions);
  const [channelVersions] = useTable('launcher', launcherTables.my_org_channel_versions);
  const [myStatus] = useTable('launcher', launcherTables.my_app_status);
  // `my_connected_clients` tracks live WebSocket connections — when the
  // launcher EXE disconnects, its row is removed by SpacetimeDB. This is
  // the authoritative "is launcher running" signal; `my_app_status.online`
  // is a heartbeat flag that goes stale on crash.
  const [connectedClients] = useTable('launcher', launcherTables.my_connected_clients);
  // Live download progress so the button can show "Downloading…" after a 202
  // (the launcher downloads a missing version before launching).
  const [downloadProgress] = useTable('launcher', launcherTables.my_download_progress);
  // #193: what is actually on THIS machine, so the primary button can read
  // Download / Update / Launch before the click rather than after a 202.
  const [installedVersions] = useTable('launcher', launcherTables.my_installed_versions);
  // Admin per-channel launch args (tier 2). Subscribed separately in
  // SpacetimeProvider; empty when that subscription is unavailable, which
  // degrades to session args only rather than blocking the launch.
  const [channelConfigs] = useTable('launcher', launcherTables.app_channel_config);

  // Resolve the app to launch: the admin's remembered per-org pick → env id
  // → "minezero" fuzzy → the org's sole own app (multi-org; see
  // resolveOrgApp in launchService). The pick exists because an org can
  // have several visible apps with no minezero match (South32 2026-09-21).
  const rememberedAppId = useStoredLaunchAppId(organizationId);
  const mineZeroApp = useMemo(
    () => resolveOrgApp(apps, MINEZERO_APP_ID, rememberedAppId),
    [apps, rememberedAppId],
  );

  // Effective app id we'll send to the launch reducer — may differ from the
  // env var when the fuzzy fallback fires above.
  const effectiveAppId = mineZeroApp?.appId ?? MINEZERO_APP_ID;

  // Webclient parity: the picker lists only channels an admin configured
  // (app_channel_config rows), labelled by their display name. With no rows
  // (none configured, or the subscription unavailable) it degrades to the
  // full enum so a dev build stays reachable.
  const channels = useMemo(() => {
    const configured = configuredChannels(channelConfigs, effectiveAppId, LAUNCHER_CHANNELS);
    return configured.length > 0 ? configured : LAUNCHER_CHANNELS;
  }, [channelConfigs, effectiveAppId]);
  // Snap a stale stored selection onto a configured channel so TraineePortal
  // and useMineZeroLaunch (same store) launch what the picker shows.
  useEffect(() => {
    if (!channels.includes(channel)) setChannel(channels[0]);
  }, [channels, channel, setChannel]);
  const channelName = channelLabel(channelConfigs, effectiveAppId, channel);

  // Version to launch for the selected channel. Follows the per-channel pinned
  // version (channel_version) first — that pin can reference a build published
  // under a different channel (e.g. Stable pinned to a Dev build), which a
  // published-channel filter alone would miss — then falls back to the latest
  // build published on the channel. Per Samuel's 2026-05-13 review: launcher
  // should let admins switch between stable and dev builds.
  const selectedVersion = useMemo(
    () => resolveLaunchVersion(appVersions, channelVersions, effectiveAppId, channel),
    [appVersions, channelVersions, channel, effectiveAppId],
  );

  // Most-recent download-progress row for the version we'd launch. Drives the
  // "Downloading… / failed" feedback after the launcher returns 202 because the
  // version wasn't installed. Picks the latest row by updatedAt so a fresh
  // attempt supersedes a stale Completed/Failed one.
  const download = useMemo(() => {
    const vid = selectedVersion?.versionId;
    if (vid === undefined) return null;
    const rows = downloadProgress.filter((r) => r.versionId === vid);
    if (rows.length === 0) return null;
    return rows.reduce((latest, r) =>
      r.updatedAt.toDate() > latest.updatedAt.toDate() ? r : latest,
    );
  }, [downloadProgress, selectedVersion?.versionId]);

  // The 202 "downloading" launch state is sticky — it has no natural end because
  // the actual download + launch happen in the launcher, not here. Clear it from
  // the live `my_download_progress` row: Completed means the launcher already
  // launched the app, Failed surfaces the error. Without this the button stays
  // "Downloading…" forever even after the app has launched.
  const downloadStatusTag = download?.status.tag;
  const downloadErrorMessage = download?.errorMessage;
  useEffect(() => {
    if (launchState.kind !== 'downloading') return;
    if (downloadStatusTag === 'Completed') {
      setLaunchState({ kind: 'idle' });
    } else if (downloadStatusTag === 'Failed') {
      setLaunchState({ kind: 'error', message: downloadErrorMessage ?? 'Download failed' });
    }
  }, [launchState.kind, downloadStatusTag, downloadErrorMessage]);

  // Launcher EXE is running iff there's a live `App`-typed connection in
  // `my_connected_clients`. This matches the TurboTwin webclient's check;
  // the older check on `my_app_status.online` showed stale "running" after
  // EXE crashes because heartbeat doesn't auto-clear.
  // #193 / #194: install state for the resolved version, and the newest
  // download for this app whatever version (progress rows carry no app_id,
  // so the app's versions are mapped in) — the indicator has to survive a
  // channel switch and an closed popover.
  const installState = useMemo(
    () =>
      resolveInstallState({
        installed: installedVersions,
        appId: effectiveAppId,
        versionId: selectedVersion?.versionId,
      }),
    [installedVersions, effectiveAppId, selectedVersion?.versionId],
  );
  const appVersionIds = useMemo(
    () =>
      new Set(
        appVersions.filter((v) => v.appId === effectiveAppId).map((v) => String(v.versionId)),
      ),
    [appVersions, effectiveAppId],
  );
  const appDownload = useMemo(
    () => latestAppDownload(downloadProgress, appVersionIds),
    [downloadProgress, appVersionIds],
  );
  const downloadActive =
    isDownloadActive(appDownload?.status) || launchState.kind === 'downloading';

  const isLauncherExeRunning = useMemo(
    () => connectedClients.some((c) => c.clientType?.tag === 'App'),
    [connectedClients],
  );

  // Surface heartbeat info for the tooltip even when the EXE isn't running.
  const lastSeenStatus = useMemo(
    () => myStatus[0],
    [myStatus],
  );

  // Four-state: the local-API probe (fed by useLauncherProbeDriver) upgrades
  // a stale-cache yellow to green (same user) or flags the launcher as
  // signed in under a different account (two browsers / two users sharing
  // one machine and its single launcher instance).
  const probe = useLauncherProbe();
  const state: HelperState = resolveHelperState({
    connActive: launcherConn.isActive,
    stdbSaysRunning: isLauncherExeRunning,
    probe,
    myEmail: user?.email ?? null,
    mySub: user?.id ?? null,
  });

  const iconColor =
    state.kind === 'connected-online'
      ? 'var(--good, #5CC89A)'
      : state.kind === 'connected-offline' || state.kind === 'running-other-user'
      ? 'var(--warn, #E8C45C)'
      : 'var(--bad, #E85C5C)';

  function handleStart() {
    // No access token is passed — the launcher EXE runs its own WorkOS
    // authorization flow. We only hand it context (where to come back to,
    // which SpacetimeDB, which org, which user) so it can complete that
    // flow scoped correctly.
    const url = addBaseUrlToProtocol({
      baseUrl: window.location.origin,
      stdbUrl: STDB_URI,
      orgId: organizationId ?? undefined,
      userHint: user?.email ?? undefined,
      // Stamp wiring: which launcher DB to heartbeat into + which WorkOS
      // client to authenticate with. Omitting these sent the launcher to the
      // house `turbotwin-launcher` / house WorkOS project, where minetwin
      // users can't log in and this portal would never see it come online.
      launcherModule: LAUNCHER_MODULE,
      workosClientId: LAUNCHER_WORKOS_CLIENT_ID,
      // Where the launcher opens the crash-report page after a crash.
      domain: WEBCLIENT_HOST,
    });
    startHelper(url);
  }

  function handleDownload() {
    const url = getLauncherDownloadUrl();
    if (url) window.open(url, '_blank', 'noopener');
  }

  async function handleLaunch() {
    const conn = launcherConn.getConnection() as LauncherDbConnection | null;
    // `versionId` is optional in queue_launch — if no app_version row matches
    // the selected channel we let the launcher pick its installed default
    // rather than block the click entirely. `appId` + `exePath` are still
    // required so the launcher has something to dispatch.
    if (!conn || !mineZeroApp) return;
    const exePath = mineZeroApp.executablePath ?? '';
    setLaunchState({ kind: 'launching' });
    // Context the Unreal app needs to reach the right backend. The launcher
    // itself only adds the user's personal args, so without these the app
    // starts on its built-in defaults and hangs on "Connection timed out"
    // (Cloud Lead 2026-07-29) — the webclient has always sent them.
    const launchArgs = composeLaunchArgs({
      orgName: orgSlug,
      serverUrl: STDB_URI,
      workosOrgId: organizationId,
      projectName,
      channelArgs: resolveChannelArgs(channelConfigs, effectiveAppId, channel),
    });
    // Fast path: hit the local launcher EXE's HTTP API. The launcher binds
    // 127.0.0.1:19871 on the user's machine while running, so this only
    // works when the EXE is up (matches our connected-online state).
    const http = await httpLaunch({
      appId: effectiveAppId,
      exePath,
      versionId: selectedVersion?.versionId,
      launchArgs,
    });
    if ('reason' in http) {
      // A non-2xx response is a real launcher error (e.g. the app row is
      // misconfigured) — surface it rather than masking it with a queued command
      // that will fail the same way. Only fall back to the SpacetimeDB queue when
      // the local API was unreachable.
      if (!http.unreachable) {
        setLaunchState({ kind: 'error', message: http.reason });
        return;
      }
      // Fallback: queue via SpacetimeDB. The launcher EXE subscribes to
      // `my_commands` and picks it up next heartbeat.
      try {
        conn.reducers.queueLaunch({
          appId: effectiveAppId,
          exePath,
          versionId: selectedVersion?.versionId,
        });
        setLaunchState({ kind: 'sent', via: 'stdb' });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setLaunchState({ kind: 'error', message });
      }
      return;
    }
    // `downloading`: the version wasn't installed; the launcher is fetching it
    // and will launch automatically. Progress is shown live from
    // `my_download_progress` below.
    setLaunchState(
      http.status === 'downloading' ? { kind: 'downloading' } : { kind: 'sent', via: 'http' },
    );
  }

  // Launch enabled when:
  //   - the launcher EXE is running (connected-online), AND
  //   - we know which app to launch (the `app` row exists)
  // versionId is optional — if no `app_version` row matches the selected
  // channel, we still allow the launch and let the launcher pick the
  // installed default.
  const launchEnabled = state.kind === 'connected-online' && Boolean(mineZeroApp);

  // Diagnostics: the set of app ids available, so when the button looks broken
  // we can immediately see which envvar to fix.
  const knownAppIds = useMemo(
    () => Array.from(new Set(apps.map((a) => a.appId))),
    [apps],
  );
  const downloadUrl = getLauncherDownloadUrl();
  const rocketTitle = downloadActive
    ? `Downloading ${selectedVersion ? `v${selectedVersion.version}` : 'build'}${
        appDownload?.percent != null ? `, ${appDownload.percent}%` : '…'
      }`
    : getStatusMessage(state);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        title={rocketTitle}
        aria-label={rocketTitle}
        aria-expanded={open ? 'true' : 'false'}
        onClick={() => setOpen((o) => !o)}
        style={{ color: downloadActive ? 'var(--amber, #E8A33D)' : iconColor }}
      >
        {/* #194: a download runs in the background with the popover shut —
            the icon itself has to say so. */}
        <span
          style={
            downloadActive
              ? { display: 'inline-flex', animation: 'pulse 1.4s ease-in-out infinite' }
              : { display: 'inline-flex' }
          }
        >
          <Icon of={Icons.view.launcher} size={16} />
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Launcher controls"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 8,
            minWidth: 240,
            // Theme tokens only — `--panel-1` never existed in the portal's
            // theme, so the old fallback hex pinned this popover to dark
            // colors in light mode.
            background: 'var(--panel)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r, 6px)',
            padding: 12,
            zIndex: 30,
            display: 'grid',
            gap: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            color: 'var(--text)',
            fontSize: 12.5,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Launcher</div>
            <div style={{ color: 'var(--muted)' }}>{getStatusMessage(state)}</div>
            {lastSeenStatus && (
              <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>
                v{lastSeenStatus.version} · {lastSeenStatus.platform}
                {!isLauncherExeRunning && ' · (offline)'}
              </div>
            )}
          </div>

          {state.kind === 'running-other-user' && (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                A launcher is running on this machine, but it is signed in as{' '}
                <strong>{state.email ?? 'a different user'}</strong>. Switch it
                to your account to launch from this tab.
              </div>
              {/* The start protocol passes user_hint=<this tab's email>; the
                  launcher forces a fresh WorkOS login when the stored token
                  belongs to someone else, so this doubles as "switch user". */}
              <GatedButton
                className="btn"
                onClick={handleStart}
                reason={launcherConn.isActive ? null : 'Connecting to the launcher service…'}
                style={primaryButtonStyle(launcherConn.isActive)}
              >
                Switch Launcher User
              </GatedButton>
            </div>
          )}

          {(state.kind === 'not-connected' || state.kind === 'connected-offline') && (
            <div style={{ display: 'grid', gap: 6 }}>
              <GatedButton
                className="btn"
                onClick={handleStart}
                reason={launcherConn.isActive ? null : 'Connecting to the launcher service…'}
                style={primaryButtonStyle(launcherConn.isActive)}
              >
                Start Launcher
              </GatedButton>
              <GatedButton
                className="btn"
                onClick={handleDownload}
                reason={downloadUrl ? null : 'Download URL not configured'}
                hint={downloadUrl ?? undefined}
                style={outlineButtonStyle(Boolean(downloadUrl))}
              >
                Download Launcher ({detectPlatform()})
              </GatedButton>
            </div>
          )}

          {state.kind === 'connected-online' && (
            <div style={{ display: 'grid', gap: 8 }}>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>
                Channel
                {channels.length === 1 ? (
                  <div style={{ color: 'var(--text)', fontSize: 12.5, marginTop: 4 }}>
                    {channelName}
                  </div>
                ) : (
                  <select
                    className="inp"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value as LauncherChannel)}
                    disabled={envForced}
                    title={
                      envForced
                        ? 'Channel is pinned by VITE_MINEZERO_CHANNEL'
                        : undefined
                    }
                    style={{ width: '100%', marginTop: 4 }}
                  >
                    {channels.map((ch) => (
                      <option key={ch} value={ch}>
                        {channelLabel(channelConfigs, effectiveAppId, ch)}
                      </option>
                    ))}
                  </select>
                )}
                {envForced && (
                  <span style={{ display: 'block', marginTop: 2 }}>
                    Pinned by config (<code>VITE_MINEZERO_CHANNEL</code>).
                  </span>
                )}
              </label>
              <GatedButton
                className="btn"
                onClick={handleLaunch}
                busy={launchState.kind === 'launching' || launchState.kind === 'downloading'}
                // Rendered only when connected-online, so the one fixable
                // block left is a missing app.
                reason={launchEnabled ? null : `No launchable app is configured for ${org.displayName}`}
                style={primaryButtonStyle(
                  launchEnabled &&
                    launchState.kind !== 'launching' &&
                    launchState.kind !== 'downloading',
                )}
                hint={
                  selectedVersion
                    ? `Launch v${selectedVersion.version}`
                    : `Launch (no ${channelName} version row — launcher will use installed default)`
                }
              >
                {launchState.kind === 'launching'
                  ? 'Launching…'
                  : launchState.kind === 'downloading'
                  ? 'Downloading…'
                  : installState === 'download'
                  ? 'Download'
                  : installState === 'update'
                  ? `Update${selectedVersion ? ` to v${selectedVersion.version}` : ''}`
                  : `Launch (${channelName})`}
              </GatedButton>
              {!mineZeroApp && apps.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--muted)', display: 'grid', gap: 4 }}>
                  <span>
                    Several apps are visible for {org.displayName} and none is
                    the obvious one — pick the app this portal should launch
                    (remembered for this organization):
                  </span>
                  {apps.map((a) => (
                    <button
                      key={a.appId}
                      type="button"
                      className="btn sm ghost"
                      style={{ justifyContent: 'flex-start' }}
                      onClick={() => setStoredLaunchAppId(organizationId, a.appId)}
                    >
                      {(a.displayName?.trim() || a.appId) + (a.displayName?.trim() ? ` (${a.appId})` : '')}
                    </button>
                  ))}
                </div>
              )}
              {!mineZeroApp && apps.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--bad, #E85C5C)' }}>
                  No launchable app is configured for {org.displayName}. An
                  administrator needs to assign an app to this organization in
                  the launcher.
                </div>
              )}
              {mineZeroApp && !selectedVersion && (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  No pinned or published version for{' '}
                  <code>{effectiveAppId}</code> on the <code>{channelName}</code>{' '}
                  channel. Launcher will use the installed default.
                </div>
              )}
              {launchState.kind === 'sent' && (
                <div style={{ fontSize: 11, color: 'var(--good, #5CC89A)' }}>
                  Launch command sent via {launchState.via === 'http' ? 'local API' : 'SpacetimeDB queue'}.
                </div>
              )}
              {/* Live download feedback from `my_download_progress`. Shown when the
                 launcher is fetching the version before launching (after a 202),
                 with progress and any failure. */}
              {download && download.status.tag !== 'Completed' && (
                download.status.tag === 'Failed' ? (
                  <div style={{ fontSize: 11, color: 'var(--bad, #E85C5C)' }}>
                    Download failed
                    {download.errorMessage ? `: ${download.errorMessage}` : ''}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    Downloading
                    {download.totalBytes > 0n
                      ? ` ${Math.min(
                          100,
                          Math.round(
                            Number((download.bytesDownloaded * 100n) / download.totalBytes),
                          ),
                        )}%`
                      : '…'}
                    {download.statusMessage ? ` · ${download.statusMessage}` : ''}
                  </div>
                )
              )}
              {launchState.kind === 'error' && (
                <div style={{ fontSize: 11, color: 'var(--bad, #E85C5C)' }}>
                  Launch failed: {launchState.message}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function primaryButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    // `--gold` / `--ink` were never theme tokens (always fell back to the
    // hex); use the portal's amber accent + a fixed dark ink that reads on
    // amber in both themes (same pairing as `.oe-btn.primary`).
    background: enabled ? 'var(--amber)' : 'var(--panel-2)',
    color: enabled ? '#0a0f18' : 'var(--muted)',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 12.5,
    cursor: enabled ? 'pointer' : 'not-allowed',
    width: '100%',
  };
}

function outlineButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    background: 'transparent',
    color: enabled ? 'var(--amber)' : 'var(--muted)',
    border: `1px solid ${enabled ? 'var(--amber)' : 'var(--border)'}`,
    padding: '6px 14px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 12.5,
    cursor: enabled ? 'pointer' : 'not-allowed',
    width: '100%',
  };
}
